/**
 * ActionRail - Single source of truth for action buttons on Today page.
 * Used in: Focus card (Locked Focus Mode), each Priority Action row, follow-up rows.
 * Order: Done (primary) | Send to phone | Call | WhatsApp | Overflow (…)
 * "Send to phone" is always visible; disabled with tooltip when lead has no phone.
 */

import { Link } from 'react-router-dom';
import { PriorityAction } from '@/lib/decisionEngine';
import { buildTelLink, buildWhatsAppLink } from '@/core/decision-engine/eliteEngine';

/** Action with optional elite contact links (from eliteEngine) */
type ActionWithContact = PriorityAction & {
  contact?: { telLink?: string | null; whatsappLink?: string | null };
};
import { ProButton } from '@/components/ui/pro-button';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeedback } from '@/hooks/useFeedback';
import {
  Phone,
  MessageCircle,
  CheckCircle,
  Clock,
  MoreHorizontal,
  ArrowUpRight,
  Smartphone,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const RAIL_BUTTON_HEIGHT = 36;
const RAIL_GAP = 8;
const RAIL_RADIUS = 9999; // pill

export interface ActionRailProps {
  action: PriorityAction;
  onCall: (action: PriorityAction) => void;
  onWhatsApp: (action: PriorityAction) => void;
  onMarkDone: (action: PriorityAction) => void;
  onDefer: (action: PriorityAction) => void;
  onSendToPhone: (action: PriorityAction, actionType: 'call' | 'whatsapp') => void;
  /** Primary CTA gets celebration + glow when true (e.g. focus card) */
  primaryCelebration?: boolean;
  /** Optional class for the rail container */
  className?: string;
  /** Stop click propagation (e.g. when inside a clickable card) */
  stopPropagation?: boolean;
}

export function ActionRail({
  action,
  onCall,
  onWhatsApp,
  onMarkDone,
  onDefer,
  onSendToPhone,
  primaryCelebration = false,
  className,
  stopPropagation = true,
}: ActionRailProps) {
  const { playCall, playWhatsApp, playMarkDone: playDone } = useFeedback();
  const hasPhone = Boolean(action.lead.phone);
  const act = action as ActionWithContact;
  const telLink = act.contact?.telLink ?? (hasPhone ? buildTelLink(action.lead.phone ?? '') : null);
  const whatsappLink = act.contact?.whatsappLink ?? (hasPhone ? buildWhatsAppLink(action.lead.phone ?? '') : null);
  const hasTelLink = Boolean(telLink);
  const hasWhatsAppLink = Boolean(whatsappLink);
  const canSendToPhone = hasTelLink || hasWhatsAppLink;

  const railProps = stopPropagation ? { onClick: (e: React.MouseEvent) => e.stopPropagation() } : {};

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          'flex items-center gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap',
          className
        )}
        style={{ minHeight: RAIL_BUTTON_HEIGHT, gap: RAIL_GAP }}
        {...railProps}
      >
        {/* Primary: Done - premium green (dark: emerald scuro, light: success tinted) */}
        <ProButton
          size="sm"
          variant="success"
          icon={CheckCircle}
          onClick={() => {
            playDone();
            onMarkDone(action);
          }}
          className={cn(
            'rounded-[var(--radius-pill)] h-9 min-w-[88px] sm:min-w-0 action-rail-btn',
            'dark:bg-emerald-950/16 dark:border dark:border-emerald-500/26 dark:text-emerald-50',
            'dark:hover:bg-emerald-950/20 dark:hover:border-emerald-500/30 dark:shadow-none dark:hover:shadow-md dark:hover:shadow-emerald-500/10',
            primaryCelebration && 'glow-accent'
          )}
          title="Mark action as done"
          celebration={primaryCelebration}
          motionTrail={primaryCelebration}
        >
          Done
        </ProButton>

        {/* Send to phone - always visible; disabled when no phone */}
        {canSendToPhone ? (
          <ProButton
            size="sm"
            variant="outline"
            icon={Smartphone}
            onClick={() => onSendToPhone(action, hasTelLink ? 'call' : 'whatsapp')}
            className="rounded-[var(--radius-pill)] h-9 px-4 min-w-[100px] sm:min-w-0 action-rail-btn"
            title="Send to phone (QR code)"
            motionTrail
          >
            <span className="hidden sm:inline">Send to phone</span>
            <span className="sm:hidden">Phone</span>
          </ProButton>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <ProButton
                  size="sm"
                  variant="outline"
                  icon={Smartphone}
                  disabled
                  className="rounded-[var(--radius-pill)] h-9 px-4 min-w-[100px] sm:min-w-0 action-rail-btn opacity-50 cursor-not-allowed"
                  aria-label="Send to phone (no phone number)"
                >
                  <span className="hidden sm:inline">Send to phone</span>
                  <span className="sm:hidden">Phone</span>
                </ProButton>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              No phone number for this lead
            </TooltipContent>
          </Tooltip>
        )}

        {hasTelLink && (
          <ProButton
            size="sm"
            variant="outline"
            icon={Phone}
            onClick={() => {
              playCall();
              onCall(action);
            }}
            className="rounded-[var(--radius-pill)] h-9 px-4 min-w-[72px] sm:min-w-0 action-rail-btn"
            title={`Call ${action.lead.name} from PC`}
            motionTrail
          >
            <span className="hidden sm:inline">Call</span>
          </ProButton>
        )}

        {hasWhatsAppLink && (
          <ProButton
            size="sm"
            variant="outline"
            icon={MessageCircle}
            onClick={() => {
              playWhatsApp();
              onWhatsApp(action);
            }}
            className="rounded-[var(--radius-pill)] h-9 px-4 min-w-[88px] sm:min-w-0 action-rail-btn"
            title={`WhatsApp ${action.lead.name} from PC`}
            motionTrail
          >
            <span className="hidden sm:inline">WhatsApp</span>
            <span className="sm:hidden">WA</span>
          </ProButton>
        )}

        {/* Overflow */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-9 h-9 p-0 rounded-[var(--radius-pill)] action-rail-btn"
              aria-label="More options"
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {hasTelLink && (
              <DropdownMenuItem onClick={() => onSendToPhone(action, 'call')}>
                <Smartphone className="w-4 h-4 mr-2" />
                Send to phone (Call)
              </DropdownMenuItem>
            )}
            {hasWhatsAppLink && (
              <DropdownMenuItem onClick={() => onSendToPhone(action, 'whatsapp')}>
                <Smartphone className="w-4 h-4 mr-2" />
                Send to phone (WhatsApp)
              </DropdownMenuItem>
            )}
            {(hasTelLink || hasWhatsAppLink) && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={() => onMarkDone(action)}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark Done
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDefer(action)}>
              <Clock className="w-4 h-4 mr-2" />
              Defer 1 day
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/app/leads?id=${action.leadId}`}>
                <ArrowUpRight className="w-4 h-4 mr-2" />
                View Lead
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  );
}
